import { useEffect } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";

import styles from "../../Modal/Modal.module.scss";
import gaugeStyles from "./CompactionContextWindowInfoModal.module.scss";

interface ContextWindowInfoModalProps {
  onClose: () => void;
  compactionFailed?: boolean;
  truncatedMessages?: number;
}

const SUMMARIZATION_EXPLANATION = [
  "Each model has a context limit, which is the maximum amount of conversation it can consider at once. Every time you send a message, the entire conversation is re-sent along with it. As a conversation continues, that space fills up.",
  "When the gauge approaches 100%, the assistant automatically summarizes older messages to free up space. After summarization, the gauge resets and the cycle begins again. This lets conversations continue indefinitely.",
  "Your original messages are never deleted. They remain visible in the chat for reference. Only the data sent to the model is trimmed. The system message is always preserved.",
];

const BEST_RESULTS_TIP =
  "For best results, start a new chat for each distinct topic. Mixing unrelated questions in one thread fills the space faster and can reduce response quality.";

export default function CompactionContextWindowInfoModal({
  onClose,
  compactionFailed = false,
  truncatedMessages = 0,
}: ContextWindowInfoModalProps) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const hasError = compactionFailed || truncatedMessages > 0;

  let headerIcon = faCircleInfo;
  let headerIconClass = gaugeStyles.headerIcon;
  let headerText = "What is Auto-Summarization?";

  if (compactionFailed && truncatedMessages > 0) {
    headerIcon = faExclamationTriangle;
    headerIconClass = gaugeStyles.errorIcon;
    headerText = "Some Messages Were Excluded";
  } else if (compactionFailed) {
    headerIcon = faExclamationTriangle;
    headerIconClass = gaugeStyles.warningIcon;
    headerText = "Auto-Summarization Failed";
  } else if (truncatedMessages > 0) {
    headerIcon = faExclamationTriangle;
    headerIconClass = gaugeStyles.errorIcon;
    headerText = "Some Messages Were Excluded";
  }

  let errorContent: React.ReactNode = null;
  if (compactionFailed && truncatedMessages > 0) {
    errorContent = (
      <>
        <p className={styles.sectionDescription}>
          {truncatedMessages} earlier message
          {truncatedMessages === 1 ? " was" : "s were"} not included in the
          model's context for this response. Each model has a context limit,
          which is the maximum amount of conversation it can consider at once.
          Normally, auto-summarization compresses older messages into a summary
          to keep them available, but it failed this turn, so they were excluded
          to stay within the context limit.
        </p>
        <p className={styles.sectionDescription}>
          Your messages are still visible in the chat. Summarization will be
          attempted again on the next message.
        </p>
        <p className={styles.sectionDescription}>
          <strong>Note:</strong> {BEST_RESULTS_TIP}
        </p>
      </>
    );
  } else if (compactionFailed) {
    errorContent = (
      <>
        <p className={styles.sectionDescription}>
          Auto-summarization failed. You still received a response, but the
          conversation was not summarized this turn.
        </p>
        <p className={styles.sectionDescription}>
          Summarization will be attempted again on the next message. No messages
          were lost.
        </p>
      </>
    );
  } else if (truncatedMessages > 0) {
    errorContent = (
      <>
        <p className={styles.sectionDescription}>
          {truncatedMessages} earlier message
          {truncatedMessages === 1 ? " was" : "s were"} dropped to fit the
          context window. The model did not see{" "}
          {truncatedMessages === 1 ? "this message" : "these messages"} when
          generating its response.
        </p>
        <p className={styles.sectionDescription}>
          Your messages are still visible in the chat for reference. Starting a
          new chat for a different topic can help avoid this.
        </p>
      </>
    );
  }

  const modalContent = (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={`${styles.sectionTitle} ${gaugeStyles.headerTitle}`}>
            <FontAwesomeIcon icon={headerIcon} className={headerIconClass} />
            {headerText}
          </h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className={styles.content}>
          {hasError ? (
            <>
              <div>{errorContent}</div>
              <details className={gaugeStyles.details}>
                <summary className={gaugeStyles.detailsSummary}>
                  What is auto-summarization?
                </summary>
                <div className={gaugeStyles.detailsContent}>
                  {SUMMARIZATION_EXPLANATION.map((text, index) => (
                    <p key={index} className={styles.sectionDescription}>
                      {text}
                    </p>
                  ))}
                  <p className={styles.sectionDescription}>
                    <strong>Note:</strong> {BEST_RESULTS_TIP}
                  </p>
                </div>
              </details>
            </>
          ) : (
            <>
              <div className={styles.section}>
                {SUMMARIZATION_EXPLANATION.map((text, index) => (
                  <p key={index} className={styles.sectionDescription}>
                    {text}
                  </p>
                ))}
                <p className={styles.sectionDescription}>{BEST_RESULTS_TIP}</p>
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>How it works</h3>
                <div className={gaugeStyles.cycle}>
                  <div className={gaugeStyles.cycleStep}>
                    <div className={gaugeStyles.cycleRing} data-fill="low" />
                    <span>Conversation grows</span>
                  </div>
                  <div className={gaugeStyles.cycleArrow}>→</div>
                  <div className={gaugeStyles.cycleStep}>
                    <div className={gaugeStyles.cycleRing} data-fill="full" />
                    <span>Gauge approaches 100%</span>
                  </div>
                  <div className={gaugeStyles.cycleArrow}>→</div>
                  <div className={gaugeStyles.cycleStep}>
                    <div className={gaugeStyles.cycleRing} data-fill="reset" />
                    <span>Summarizes &amp; resets</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.secondaryButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
